/**
 * useDualCtaLabels — hook que fetcha los labels custom del Dual CTA (y el video
 * por estado) desde /api/dual-cta-labels (admin DB) y devuelve null si no hay override.
 *
 * Consumido por BaseTemplateInternationalDual + DualCTAVideoLanding +
 * USExclusiveLanding como FALLBACK del snapshot del worker (cold start / primer
 * paint antes de que cargue /api/cta-config-snapshot).
 *
 * Geo-aware: acepta { country, state, province, city } y la API resuelve por la
 * MISMA cascada que el worker — city_ar (AR) > city > province_ar (AR) >
 * state_us (US) > country > DEFAULT — para que el fallback matchee la variante
 * que el snapshot va a servir (sin flash de labels/video equivocados).
 *
 * Fallback chain en el caller:
 *   1. snapshot del worker (useLiveCTA) — primario, geo-correcto
 *   2. label/video custom de admin (este hook) — fallback geo-aware
 *   3. default i18n / env video
 *
 * Cache: SWR-like via state. Edge cache de 60s en la API (por URL → por geo).
 */

import { useEffect, useMemo, useState } from "react";
import { getServerTemplateConfig } from "./serverTemplateDetection";

export interface DualCtaLabels {
  paid: string | null;
  paidSubtitle: string | null;
  free: string | null;
  /** Mux playback id de la variante resuelta por geo (video por estado). null = usar env. */
  muxPlaybackId: string | null;
  /** Toggle free Telegram de la variante resuelta (paridad con snapshot del worker). */
  freeEnabled?: boolean | null;
  /** URL del grupo Telegram free de la variante. */
  freeTelegramUrl?: string | null;
  /** Precio display USD de la variante. */
  priceUsd?: number | null;
  /** Stripe Price ID (mensual recurrente) de la variante. */
  stripePriceId?: string | null;
  source: "admin_db" | "defaults";
  /** Lang base que la API resolvió ("es"|"en"|"pt"|"de"|"fr"|"it"). Útil para debug. */
  lang?: string;
  /** Geo resuelto por la API ("state_us:FL" | "country:US" | "DEFAULT"). Debug. */
  geo?: string;
}

export interface DualCtaGeo {
  country?: string | null;
  state?: string | null;
  province?: string | null;
  city?: string | null;
}

const EMPTY: DualCtaLabels = {
  paid: null,
  paidSubtitle: null,
  free: null,
  muxPlaybackId: null,
  source: "defaults",
};

// Module-level cache por (lang|country|state). TTL 60s (mismo del edge cache).
const _cache = new Map<string, { value: DualCtaLabels; expiresAt: number }>();
const TTL_MS = 60_000;

function cacheKey(lang: string, geo?: DualCtaGeo): string {
  const country = (geo?.country || "").trim().toUpperCase();
  const state = (geo?.state || "").trim().toUpperCase();
  const province = (geo?.province || "").trim().toLowerCase();
  const city = (geo?.city || "").trim().toLowerCase();
  return `${lang}|${country}|${state}|${province}|${city}`;
}

/**
 * Geo por defecto: si el caller no pasa `geo`, lo deriva del __TEMPLATE_CONFIG__
 * que inyecta el worker (country + usState + province + city). Así TODAS las
 * superficies dual-CTA resuelven la variante por ciudad/provincia/estado/país en
 * el fallback sin tener que cablear cada caller. Best-effort: sin window o sin
 * config → {} (DEFAULT). VERDAD ÚNICA: todo geo sale del Worker.
 */
function readWorkerGeo(): DualCtaGeo {
  if (typeof window === "undefined") return {};
  try {
    const c = getServerTemplateConfig();
    return {
      country: c?.country ?? null,
      state: c?.usState ?? null,
      province: c?.province ?? null,
      city: c?.city ?? null,
    };
  } catch {
    return {};
  }
}

async function fetchLabels(lang: string, geo: DualCtaGeo | undefined, key: string): Promise<DualCtaLabels> {
  const cached = _cache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  try {
    const params = new URLSearchParams({ lang });
    const country = (geo?.country || "").trim();
    const state = (geo?.state || "").trim();
    const province = (geo?.province || "").trim();
    const city = (geo?.city || "").trim();
    if (country) params.set("country", country);
    if (state) params.set("state", state);
    if (province) params.set("province", province);
    if (city) params.set("city", city);
    const res = await fetch(`/api/dual-cta-labels?${params.toString()}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      _cache.set(key, { value: EMPTY, expiresAt: Date.now() + TTL_MS });
      return EMPTY;
    }
    const raw = (await res.json()) as Partial<DualCtaLabels>;
    const data: DualCtaLabels = {
      paid: raw.paid ?? null,
      paidSubtitle: raw.paidSubtitle ?? null,
      free: raw.free ?? null,
      muxPlaybackId: raw.muxPlaybackId ?? null,
      freeEnabled: raw.freeEnabled ?? null,
      freeTelegramUrl: raw.freeTelegramUrl ?? null,
      priceUsd: raw.priceUsd ?? null,
      stripePriceId: raw.stripePriceId ?? null,
      source: raw.source ?? "defaults",
      lang: raw.lang,
      geo: raw.geo,
    };
    _cache.set(key, { value: data, expiresAt: Date.now() + TTL_MS });
    return data;
  } catch {
    _cache.set(key, { value: EMPTY, expiresAt: Date.now() + TTL_MS });
    return EMPTY;
  }
}

/**
 * Hook reactivo. Acepta el lang base (es/en/etc) y, opcional, el geo del visitor
 * (country + US state) para que la API resuelva la variante por estado. En SSR
 * (no window) devuelve EMPTY → templates usan defaults i18n.
 */
export function useDualCtaLabels(lang: string, geo?: DualCtaGeo): DualCtaLabels {
  const resolved = geo ?? readWorkerGeo();
  const key = cacheKey(lang, resolved);
  const country = (resolved.country || "").trim() || null;
  const state = (resolved.state || "").trim() || null;
  const province = (resolved.province || "").trim() || null;
  const city = (resolved.city || "").trim() || null;

  const initial = useMemo<DualCtaLabels>(() => {
    const cached = _cache.get(key);
    return cached && cached.expiresAt > Date.now() ? cached.value : EMPTY;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const [labels, setLabels] = useState<DualCtaLabels>(initial);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    void fetchLabels(lang, { country, state, province, city }, key).then((v) => {
      if (!cancelled) setLabels(v);
    });
    return () => {
      cancelled = true;
    };
    // `key` ya encodea lang+geo completo; country/state/province/city van
    // aparte solo para pasarlos al fetch sin re-derivar.
  }, [lang, country, state, province, city, key]);

  return labels;
}
